import { useEffect, useState } from 'react'

import styled from '@emotion/styled'
import { formatDistanceToNow } from 'date-fns'

import { Box, Flex, FlexGrow, FlexNone, Position, spacing, Text } from '@cash-design-system/react'
import { DialogScrollableBackground } from '@cash-web/shared-feature-modal'

import { useCommandKSearch } from './useCommandKSearch'

const TopPosition = styled(Position)`
  z-index: 99999;
`

const AnimateIn = styled.div`
  animation: slidein 225ms ease-in-out;

  @keyframes slidein {
    from {
      transform: translateY(-0.75rem);
      opacity: 0;
    }

    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
`

const FullWidthPosition = styled(Position)`
  width: 100%;
`

const Height = styled.div`
  height: 100%;
  max-width: 36rem;
  padding-top: 10rem;
  position: relative;
  width: 100%;
`

const ShadowBox = styled(Box)`
  box-shadow: 0 0.5rem 1rem rgb(0 0 0 / 30%);
  width: 100%;
`

const Input = styled.input`
  all: unset;
  color: ${props => props.theme.arcadeColors.component.inputTextDefault};
  width: 100%;
`

const OverflowBox = styled(Box)`
  overflow: hidden;
`

const ScrollBox = styled(Box)`
  max-height: 24rem;
  overflow: auto;
`

const MenuItem = styled.div<{ isSelected?: boolean }>`
  background-color: ${props => (props.isSelected ? props.theme.arcadeColors.component.cellBackgroundHover : 'none')};
  font-size: 0.875rem; /* stylelint-disable-line @cash-web/no-hardcoded-font-styles */
  padding: 0.5rem 1rem;
  cursor: pointer;

  &:active {
    background-color: ${props => props.theme.arcadeColors.component.cellBackgroundPressed};
  }
`

const OverflowFlexGrow = styled(FlexGrow)`
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const Menu = ({ children }: { children: React.ReactNode }) => (
  <FullWidthPosition position="absolute">
    <Box padding="1rem 0 0 0">
      <OverflowBox padding="0.5rem 0" backgroundColor="cardBackground" borderRadius="0.5rem">
        <ScrollBox>{children}</ScrollBox>
      </OverflowBox>
    </Box>
  </FullWidthPosition>
)

type SearchProps = { setIsOpen: (isOpen: boolean) => void }
const Search = ({ setIsOpen }: SearchProps) => {
  const { value, onChange, onKeyDown, menuItems, selectedMenuItem, setSelectedMenuItem, navigate } = useCommandKSearch({
    setIsOpen,
  })

  return (
    <AnimateIn>
      <ShadowBox
        padding="1rem 1.5rem"
        backgroundColor="cardBackground"
        borderRadius="0.5rem"
        onClick={e => e.stopPropagation()}
      >
        <Input
          type="text"
          placeholder="Where do you want to go?"
          autoFocus
          value={value}
          onChange={onChange}
          onKeyDown={onKeyDown}
        />
      </ShadowBox>
      {menuItems.length > 0 ? (
        <Menu>
          {menuItems.map(item => (
            <MenuItem
              key={`${item.name}${item.path}${item.id}`}
              isSelected={
                selectedMenuItem?.name === item.name &&
                selectedMenuItem?.path === item.path &&
                selectedMenuItem?.id === item.id
              }
              onClick={() => navigate(item.path)}
              onMouseEnter={() => setSelectedMenuItem(item)}
              onMouseLeave={() => setSelectedMenuItem(null)}
            >
              <Flex gap={spacing.m}>
                <OverflowFlexGrow>
                  <Text typography="bodySmall" color="textStandard">
                    {item.name}
                  </Text>
                </OverflowFlexGrow>
                {item.timeStamp ? (
                  <FlexNone>
                    <Text typography="metaText">
                      {formatDistanceToNow(new Date(item.timeStamp), { addSuffix: true })}
                    </Text>
                  </FlexNone>
                ) : null}
              </Flex>
            </MenuItem>
          ))}
        </Menu>
      ) : null}
    </AnimateIn>
  )
}

export const CommandK = () => {
  const [isOpen, setIsOpen] = useState(false)

  const handleKeyDown = (event: KeyboardEvent) => {
    if ((event.metaKey || event.ctrlKey) && event.key === 'k') setIsOpen(true)
    if (event.key === 'Escape') setIsOpen(false)
  }

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => setIsOpen(false)
    window.addEventListener('click', handleClickOutside)
    return () => window.removeEventListener('click', handleClickOutside)
  }, [])

  return isOpen ? (
    <TopPosition position="fixed">
      <DialogScrollableBackground>
        <Height>
          <Search setIsOpen={setIsOpen} />
        </Height>
      </DialogScrollableBackground>
    </TopPosition>
  ) : null
}
